import React, { useContext } from 'react';
import c from 'classnames';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { ModalRoot, ModalRootProps } from '../../base';
import HeaderlessLayout from './HeaderlessLayout';
import { ModalLayoutProps } from '../Modal/ModalLayout';

/*
 * This is a lobotomized version of /dsPresets/Modal
 * DT is opposed to headerless modals, but muro got a pass because its modal is
 * almost like a mini app, and not very modal-like. So, rather than make dsModal have
 * an option to remove the header (and pave the way for UX requesting other things do
 * that), a headerless modal is put in its own separate thing.
 *
 * Don't use this unless your modal is not a normal modal, but really more of an app.
 */
export interface Props
  // Partial here because TS has a bug that doesn't preserve optional-ness when extending and `Pick`ing
  extends
    Pick<
      ModalRootProps,
      | 'onRequestClose'
      | 'useFixedMobileHeight'
      | 'isFullscreen'
      | 'hasCloseButton'
      | 'useTransparentOverlay'
      | 'bodyScrollLockOptions'
      | 'contentLabel'
      | 'className'
    >,
    Pick<
      ModalLayoutProps,
      'innerScrollRef' | 'contentClassName' | 'headerPadding' | 'footerPadding' | 'onSubmit'
    > {
  /** If `true`, this will show the modal. */
  isOpen: boolean;

  /** Wrap content in a scrollable div. Default is true */
  withScrollableContentWrapper?: boolean;

  /** Styles for the ModalLayout component */
  layoutClassName?: string;

  /**
   * If this modal is opened from within another modal/popover that doesn't close when this one mounts,
   * we need to use this prop or clicks on this modal's content will close the whole stack.
   * */
  ignoreClickOutside?: boolean;

  /**
   * Enable ipad mode which deal with ipads being half desktop
   */
  ipadMode?: boolean;
}

/** Our Standard modal. Has a heading, subheading, close button, and footer.  */
const HeaderlessModal: React.FC<React.PropsWithChildren<Props>> = ({
  isOpen,
  headerPadding,
  footerPadding,
  children,
  className,
  contentClassName,
  innerScrollRef,
  withScrollableContentWrapper = true,
  isFullscreen: isFullscreenProp,
  layoutClassName,
  ignoreClickOutside,
  ipadMode = false,
  onSubmit,
  ...rest
}) => {
  const isMobile = useContext(MobileContext);
  const enableIpad =
    ipadMode &&
    typeof document !== 'undefined' &&
    navigator.userAgent.includes('Mac') &&
    'ontouchend' in document;
  const isFullscreen = (isFullscreenProp ?? isMobile) || enableIpad;

  return (
    <MobileContext.Provider value={isMobile || (enableIpad ?? false)}>
      <ModalRoot
        ipadMode={ipadMode}
        isOpen={isOpen}
        className={c(className, ignoreClickOutside && 'ignore-react-onclickoutside')}
        hasCloseButton={false}
        isFullscreen={isFullscreen}
        shouldCloseOnOverlayClick={false}
        {...(rest as any)}
      >
        <HeaderlessLayout
          className={layoutClassName}
          isFullscreen={isFullscreen}
          withScrollableContentWrapper={withScrollableContentWrapper}
          innerScrollRef={innerScrollRef}
          content={children}
          contentClassName={contentClassName}
          onSubmit={onSubmit}
        />
      </ModalRoot>
    </MobileContext.Provider>
  );
};

HeaderlessModal.displayName = 'HeaderlessModal';
export default HeaderlessModal;
